import { FC } from "react";
import { useTrans } from "../../../hooks";
import { useAuthData } from "../../../contexts";
import { Text, HStack, Badge, Button } from "@chakra-ui/react";
import {
    DataType,
    IKaTableProps,
    ITableInstance,
    Table as KaTable,
    SortingMode,
} from "ka-table";
import { Column, PagingOptions } from "ka-table/models";
import { ICellTextProps } from "ka-table/props";
import {
    AppIcon,
    AppOptionBoxItem,
    AppOptionsBox,
    KaPageSizeSelector,
} from "../../../components";
import { APPC, UTIL } from "../../../config";

export const InternshipScheduleList: FC<{
    table: ITableInstance;
    paging: PagingOptions;
    list: any[];
    loadingList?: boolean;
    onActionClick?: (key: string, data?: any) => void;
}> = ({
    table,
    paging,
    list,
    loadingList = false,
    onActionClick = () => {},
}) => {
    const { t } = useTrans();
    const { grant } = useAuthData();
    const isOptionDisable = grant.isCompanyDirector || grant.isInternshipTutor;
    const dayjs = UTIL.getDayJs();
    const columns: Column[] = [
        {
            key: "name",
            title: t("ent.InternshipSchedule:name.label"),
            dataType: DataType.String,
        },
        {
            key: "capacity",
            title: t("ent.InternshipSchedule:capacity.label"),
            dataType: DataType.Number,
        },
        {
            key: "startDate",
            title: t("ent.InternshipSchedule:startDate.label"),
            dataType: DataType.String,
        },
        {
            key: "endDate",
            title: t("ent.InternshipSchedule:endDate.label"),
            dataType: DataType.String,
        },
        {
            key: "startTime",
            title: t("ent.InternshipSchedule:startTime.label"),
            dataType: DataType.String,
        },
        {
            key: "endTime",
            title: t("ent.InternshipSchedule:endTime.label"),
            dataType: DataType.String,
        },
        {
            key: "breakDuration",
            title: t("ent.InternshipSchedule:breakDuration.label"),
            dataType: DataType.Number,
        },
        {
            key: "totalTime",
            title: t("ent.InternshipSchedule:totalTime.label"),
            dataType: DataType.Number,
        },
        {
            key: "isScheduleDetail",
            title: t("ent.InternshipSchedule:isScheduleDetail.label"),
            dataType: DataType.Boolean,
        },
        {
            key: "action",
            title: "",
            style: { textAlign: "right" },
            dataType: DataType.Object,
            width: 50,
            isSortable: false,
        },
    ];

    const actionOptions: AppOptionBoxItem[] = [
        {
            key: "timesheet",
            label: t("padm.InternshipSchedulePage:text.option.timesheet"),
            iconName: "icl-spreadsheet",
        },
        {
            key: "students",
            label: t("padm.InternshipSchedulePage:text.option.students"),
            iconName: "icl-user",
        },
        {
            key: "internshipScheduleSkill",
            label: t("padm.InternshipSchedulePage:text.option.skill"),
            iconName: "icl-lightbulb",
        },
        {
            key: "clone",
            label: t("cmn:option.clone"),
            iconName: "icl-clone",
            isDisabled: isOptionDisable,
        },
        {
            key: "edit",
            label: t("cmn:option.edit"),
            iconName: "icl-pencil",
            isDisabled: isOptionDisable,
        },
        {
            key: "delete",
            label: t("cmn:option.delete"),
            iconName: "icl-trash",
            isDisabled: isOptionDisable,
        },
    ];

    const kaTableProps: IKaTableProps = {
        table: table,
        columns: columns,
        data: list || [],
        loading: {
            enabled: loadingList,
        },
        paging: paging,
        rowKeyField: "id",
        sortingMode: SortingMode.SingleRemote,
        childComponents: {
            pagingSizes: {
                content: (props) => <KaPageSizeSelector {...props} />,
            },
            cellText: {
                content: (props: ICellTextProps) => {
                    switch (props?.column?.key) {
                        case "name":
                            return (
                                <Text
                                    textStyle={"mediumSm"}
                                    color={"var(--chakra-colors-black)"}
                                >
                                    {props?.rowData?.name}
                                </Text>
                            );
                        case "capacity":
                            return (
                                <Text>
                                    {`${props?.rowData?.totalUsedSeats} / ${props?.rowData?.capacity}`}
                                </Text>
                            );
                        case "startDate":
                            return (
                                <Text>
                                    {`${dayjs
                                        .utc(props?.rowData?.startDate)
                                        .format(APPC.APP_DATE_FORMAT)}`}
                                </Text>
                            );
                        case "endDate":
                            return (
                                <Text>
                                    {`${dayjs
                                        .utc(props?.rowData?.endDate)
                                        .format(APPC.APP_DATE_FORMAT)}`}
                                </Text>
                            );
                        case "startTime":
                            return (
                                <Text>
                                    {`${dayjs
                                        .utc(props?.rowData?.startTime)
                                        .format(APPC.APP_TIME_FORMAT)}`}
                                </Text>
                            );
                        case "endTime":
                            return (
                                <Text>
                                    {`${dayjs
                                        .utc(props?.rowData?.endTime)
                                        .format(APPC.APP_TIME_FORMAT)}`}
                                </Text>
                            );
                        case "isScheduleDetail":
                            return (
                                <Badge
                                    variant={
                                        props?.rowData?.isScheduleDetail
                                            ? "green"
                                            : "red"
                                    }
                                >
                                    {t(
                                        `cmn:label.${props?.rowData?.isScheduleDetail ? "yes" : "no"}`
                                    )}
                                </Badge>
                            );
                        case "breakDuration":
                            return (
                                <Text>
                                    {props?.rowData?.breakDuration}{" "}
                                    {t("cmn:label.min")}
                                </Text>
                            );
                        case "totalTime":
                            return (
                                <Text>
                                    {props?.rowData?.totalTime / 60}{" "}
                                    {t("cmn:label.hours")}
                                </Text>
                            );
                        case "action":
                            return (
                                <HStack justify={"flex-end"} gap={3}>
                                    <Button
                                        variant={"outline"}
                                        className="btn-icon-xs"
                                        size="xs"
                                        title={t(
                                            "padm.InternshipScheduleSeatPage:text.option.seats"
                                        )}
                                        onClick={() =>
                                            onActionClick(
                                                "internshipScheduleSeat",
                                                props?.rowData
                                            )
                                        }
                                        disabled={
                                            isOptionDisable ||
                                            grant.isInternshipSupervisor
                                        }
                                    >
                                        <AppIcon name="icl-couch" w="0.8rem" />
                                    </Button>
                                    <AppOptionsBox
                                        options={actionOptions}
                                        data={props?.rowData}
                                        onClick={onActionClick}
                                    />
                                </HStack>
                            );
                    }
                },
            },
        },
    };

    return <KaTable {...kaTableProps} />;
};
